import React, { useState, useEffect, useMemo } from 'react';
import Grid from '@material-ui/core/Grid';
import { fromEvent } from 'rxjs';
import { scan, map, debounceTime } from 'rxjs/operators';
import { PropTypes } from 'prop-types';
import moment from 'moment';
import { getProductsInfos, errorClear } from 'actions/ProductsInfo';
import { fetchDistributionCenters, selectDistributionCenter } from 'actions/DistributionCenters';
import { connect } from 'react-redux';
import { sendNotification, errorNotification } from 'actions/Notifications';
import { save } from 'actions/Counting';
import Title from '../components/Title';
import SubTitle from '../components/SubTitle';
import DcBox from '../components/DcBox';
import ProductBox from '../components/ProductBox';
import LoadingDcs from '../components/LoadingDcs';
import Totals from '../components/Totals';
import ManualButton from '../components/ManualButton';
import ButtonsFooter from '../components/ButtonsFooter';
import ConfirmModal from '../components/ConfirmModal';
import SinglePackageAs20Radio from '../components/SinglePackageAs20Radio';
import {
	ALREADY_READ_ERROR,
	EXPIRED_BATCH_SEGREGATED,
	NEAR_EXPIRATION_BATCH_SEGREGATED,
	REJECTED_BATCH_SEGREGATED,
} from '../constants/errors';

const BUSY = 200;

const triggerBusy = setBusy => {
	setBusy(true);
	setTimeout(() => {
		setBusy(false);
	}, BUSY);
};

const inventoryTypes = {
	chamber: 'Câmara fria | Freezers de estoque',
	picking: 'Freezers de Picking',
	snacks: 'Snacks',
};
const formatBatch = batch => `20${batch.slice(-2)}${batch.slice(2, -2)}${batch.slice(0, 2)}`;

// Espelha o `skuFirstLetterAlphabetPosition`/`paddedSkuFirstLetterAlphabetPosition` do
// inventory-api, usados para gerar o lote sintético de um código de caixa (ver `generateBatch`).
const paddedSkuFirstLetterAlphabetPosition = letter =>
	String(letter.toUpperCase().charCodeAt(0) - 64).padStart(2, '0');

// Lote sintético equivalente ao gerado no backend para códigos de caixa (22 chars): prefixo fixo
// "2" (mesma convenção usada lá) + posição alfabética do SKU + números do SKU + validade em DDMMYY.
// É esse identificador que casa com o `code` de um lote bloqueado pela qualidade.
const generateSyntheticBatchForBoxCode = (sku, expirationDate) =>
	`2${paddedSkuFirstLetterAlphabetPosition(sku.charAt(0))}${sku.slice(1)}${moment(
		expirationDate
	).format('DDMMYY')}`;

// Espelha `isWithinShelfLife` do logistics-api (controllers/counting/validation.js), usada tanto
// no back quanto aqui para decidir vencido (withdrawalPeriod=0) e próximo do vencimento
// (withdrawalPeriod=cadastrado). Fronteira deliberadamente inclusiva do lado "ainda válido":
// no dia em que restam exatamente `withdrawalPeriod` dias até o vencimento, o back ainda
// considera o lote dentro do prazo (isSameOrAfter) e não bloqueia — reproduzir esse mesmo
// corte aqui evita que a contagem rejeite, na loja, um lote que o back aceitaria (e vice-versa).
const isWithinShelfLife = (expirationDate, today, withdrawalPeriod) =>
	expirationDate.clone().subtract(withdrawalPeriod, 'days').isSameOrAfter(today, 'day');

const getInfoFromScannedCode = (barcode, singlePackageCountingAsBag, products) => {
	if (barcode.length === 22) {
		const sku = barcode.slice(2, 6);
		const manufacturingDate = barcode.slice(9, 17);
		const product = products[sku];
		// código de caixa só carrega a data de fabricação: a validade depende do shelf life
		// (`expiration`, em dias) cadastrado no produto. Sem esse cadastro não há como calculá-la.
		const expirationDate = Number.isFinite(product && product.expiration)
			? moment(manufacturingDate, 'YYYYMMDD').add(product.expiration, 'days')
			: null;
		return {
			sku,
			batch: manufacturingDate,
			quantity: Number(barcode.slice(6, 9)),
			expirationDate,
		};
	}
	const batch = formatBatch(barcode.slice(6, 12));
	return {
		sku:
			String.fromCharCode(96 + parseInt(barcode.substr(1, 2))).toUpperCase() +
			barcode.substr(3, 3),
		batch,
		quantity: singlePackageCountingAsBag ? 20 : 1,
		// o código de lote curto já codifica a própria validade (não a fabricação)
		expirationDate: moment(batch, 'YYYYMMDD'),
	};
};

// Identificador do lote usado para consultar a lista de lotes bloqueados pela qualidade
// (`rejectedBatches`): para caixa, é o lote sintético calculado a partir do SKU + validade;
// para lote curto, é o próprio código lido (sem o dígito verificador).
const getRejectedBatchLookupCode = (scannedCode, sku, expirationDate) => {
	if (scannedCode.length === 22) {
		return expirationDate ? generateSyntheticBatchForBoxCode(sku, expirationDate) : null;
	}
	return scannedCode.length > 12 ? scannedCode.slice(0, -1) : scannedCode;
};

const CountingContainer = props => {
	const [step, setStep] = useState(0);
	const [dc, setDc] = useState();
	const [items, setItems] = useState({});
	const [busy, setBusy] = useState(false);
	const [total, setTotal] = useState(0);
	const [lastProduct, setLastProduct] = useState();
	const [inventoryType, setInventoryType] = useState();
	const [confirmModal, setConfirmModal] = useState(false);
	const [manual, setManual] = useState(false);
	const [barcodes, setBarcodes] = useState({});
	const [segregatedItems, setSegregatedItems] = useState({});
	const [segregatedTotal, setSegregatedTotal] = useState(0);
	const [singlePackageCountingAsBag, setSinglePackageCountingAsBag] = useState(false);

	// Só considera efetivamente rejeitado um lote com `rejected` truthy — mesmo critério do backend
	// (controllers/counting/validation.js#loadRejectedBatchCodes), evitando divergência: `rejected`
	// ausente/undefined não bloqueia (só uma rejeição de fato marcada trava).
	const rejectedBatchCodes = useMemo(
		() =>
			new Set(
				(props.rejectedBatches || [])
					.filter(rejectedBatch => rejectedBatch.rejected)
					.map(rejectedBatch => rejectedBatch.code)
			),
		[props.rejectedBatches]
	);

	const resetState = () => {
		setStep(0);
		setItems({});
	};
	const handleTagScan = scannedCode => {
		triggerBusy(setBusy);
		const scanInfo = getInfoFromScannedCode(scannedCode, singlePackageCountingAsBag, props.products);
		let { sku, quantity, batch } = scanInfo;
		const { expirationDate } = scanInfo;
		if (!props.products[sku]) {
			const product = Object.keys(props.products).find(
				p => String(props.products[p].barcode) === scannedCode
			);
			if (
				!product ||
				(inventoryType === Object.keys(inventoryTypes)[0] &&
					!manual &&
					!singlePackageCountingAsBag)
			) {
				return props.sendNotification({
					type: 'error',
					message: 'Produto não encontrado',
				});
			}

			if (singlePackageCountingAsBag && !product.productsPerBag) {
				return props.sendNotification({
					type: 'error',
					message: `Esse produto não possui o campo "Número de produtos por saco" cadastrado.`,
				});
			}
			quantity = singlePackageCountingAsBag ? product.productsPerBag : 1;

			sku = product;
			batch = '';
			setLastProduct(props.products[sku].name);
			setTotal(total + quantity);
			setBarcodes({
				...(barcodes || {}),
				...(scannedCode.length === 22 && { [scannedCode]: scannedCode }),
			});
			setItems({
				...items,
				[sku]: {
					sku,
					name: props.products[sku].name,
					units:
						((items && items[sku] && items[sku].units) || 0) +
						(((quantity === 1 || singlePackageCountingAsBag) && quantity) || 0),
					batches: {
						...((items && items[sku] && items[sku].batches) || {}),
						...(batch && batch !== '' && { [batch]: batch }),
					},
				},
			});
			return;
		}
		if (
			(inventoryType === Object.keys(inventoryTypes)[1] || manual) &&
			scannedCode.length > 13
		) {
			return props.sendNotification({
				type: 'error',
				message: 'Esse não é um código de saquinho',
			});
		}
		if (
			(!inventoryType || inventoryType === Object.keys(inventoryTypes)[0]) &&
			!manual &&
			!singlePackageCountingAsBag &&
			scannedCode.length < 20
		) {
			return props.sendNotification({
				type: 'error',
				message: 'Esse não é um código de caixa/saco',
			});
		}
		if (scannedCode.length === 22 && barcodes && barcodes[scannedCode]) {
			return props.errorNotification({ message: ALREADY_READ_ERROR });
		}
		if (isNaN(quantity)) {
			return props.sendNotification({
				type: 'error',
				message: 'Erro na leitura, leia novamente por favor',
			});
		}

		const scannedProduct = props.products[sku];

		if (singlePackageCountingAsBag && !scannedProduct.productsPerBag) {
			return props.sendNotification({
				type: 'error',
				message: `Esse produto não possui o campo "Número de produtos por saco" cadastrado.`,
			});
		}

		let segregationMessage = null;
		if (expirationDate) {
			const today = moment().startOf('day');
			const withdrawalPeriod = Number.isFinite(scannedProduct.withdrawalPeriod)
				? scannedProduct.withdrawalPeriod
				: 0;
			if (!isWithinShelfLife(expirationDate, today, 0)) {
				segregationMessage = EXPIRED_BATCH_SEGREGATED;
			} else if (
				withdrawalPeriod > 0 &&
				!isWithinShelfLife(expirationDate, today, withdrawalPeriod)
			) {
				segregationMessage = NEAR_EXPIRATION_BATCH_SEGREGATED;
			}
		}

		const batchCode = getRejectedBatchLookupCode(scannedCode, sku, expirationDate);
		if (!segregationMessage && batchCode && rejectedBatchCodes.has(batchCode)) {
			segregationMessage = REJECTED_BATCH_SEGREGATED;
		}

		const isPackageCode = scannedCode.length === 22;
		quantity =
			singlePackageCountingAsBag && !isPackageCode ? scannedProduct.productsPerBag : quantity;
		const manualUnits = isPackageCode
			? 0
			: ((quantity === 1 || singlePackageCountingAsBag) && quantity) || 0;
		setLastProduct(scannedProduct.name);
		setBarcodes({
			...(barcodes || {}),
			...(scannedCode.length === 22 && { [scannedCode]: scannedCode }),
		});

		if (segregationMessage) {
			const savedSegregated = (segregatedItems && segregatedItems[sku]) || {};
			const savedBatches = savedSegregated.batches || {};
			setSegregatedTotal(segregatedTotal + quantity);
			setSegregatedItems({
				...segregatedItems,
				[sku]: {
					sku,
					name: scannedProduct.name,
					batches: {
						...savedBatches,
						...(manualUnits > 0 &&
							batchCode && {
								[batchCode]: (savedBatches[batchCode] || 0) + manualUnits,
							}),
					},
				},
			});
			setItems({
				...items,
				[sku]: {
					sku,
					name: scannedProduct.name,
					units: (items && items[sku] && items[sku].units) || 0,
					batches: (items && items[sku] && items[sku].batches) || {},
				},
			});
			return props.sendNotification({
				type: 'warning',
				message: segregationMessage,
				autoDismiss: 0,
			});
		}

		setTotal(total + quantity);
		setItems({
			...items,
			[sku]: {
				sku,
				name: scannedProduct.name,
				units: ((items && items[sku] && items[sku].units) || 0) + manualUnits,
				batches: {
					...((items && items[sku] && items[sku].batches) || {}),
					[batch]: batch,
				},
			},
		});
	};

	useEffect(() => {
		props.fetchDistributionCenters();
		props.getProductsInfos({ rejectedBatches: true });
		return () => {};
	}, []);

	useEffect(() => {
		if (props.saveSuccess) {
			props.getProductsInfos({ rejectedBatches: true });
			setTotal(0);
			setItems({});
			setLastProduct();
			setBarcodes({});
			setSegregatedItems({});
			setSegregatedTotal(0);
			setManual(false);
			setSinglePackageCountingAsBag(false);
			if (dc.kind !== 'Warehouse') {
				setInventoryType();
				setStep(step - 1);
			}
		}
		return () => {};
	}, [props.saveSuccess]);

	const handlePaste = ({ clipboardData }) => {
		const barcode = clipboardData.getData('Text');
		handleTagScan(barcode);
	};

	useEffect(() => {
		if (step === 2) {
			if (!busy) {
				window.addEventListener('paste', handlePaste);
				const obs = fromEvent(window, 'keypress')
					.pipe(
						map(event => {
							const { keyCode } = event;
							if (keyCode === 13) {
								return '';
							}
							return String.fromCharCode(keyCode);
						}),
						scan((acc, cur) => `${acc}${cur}`, ''),
						debounceTime(100)
					)
					.subscribe(handleTagScan);
				return () => {
					window.removeEventListener('paste', handlePaste);
					obs.unsubscribe();
				};
			}
			return () => {};
		}
		return () => {};
	});

	useEffect(() => {
		if (
			props.distributionCenters &&
			props.distributionCenters.data &&
			props.distributionCenters.data.length > 0 &&
			localStorage.getItem('distributionCenter')
		) {
			const distributionCenter = props.distributionCenters.data.find(
				d => d._id === localStorage.getItem('distributionCenter')
			);
			if (distributionCenter.kind === 'Warehouse') {
				setStep(2);
			} else {
				setStep(1);
			}
			setDc(distributionCenter);
		}
		return () => {};
	}, [props.distributionCenters]);

	const handleClick = name => {
		const distributionCenter = props.distributionCenters.data.find(d => d.name === name);
		if (distributionCenter.kind === 'Warehouse') {
			setStep(2);
		} else {
			setStep(1);
		}
		props.selectDistributionCenter(
			distributionCenter._id,
			distributionCenter.address,
			distributionCenter.kind
		);
		setDc(distributionCenter);
	};

	const handleSelectType = type => {
		setInventoryType(Object.keys(inventoryTypes).find(key => inventoryTypes[key] === type));
		setStep(2);
	};

	const handleBack = () => {
		if (dc.kind === 'Warehouse') {
			setStep(step - 2);
		} else {
			setStep(step - 1);
		}
		if (step - 1 === 1) {
			setInventoryType();
		}
		if (step - 1 === 0) {
			setDc();
		}
		setSinglePackageCountingAsBag(false);
		setManual(false);
		setTotal(0);
		setItems({});
		setLastProduct();
		setBarcodes({});
		setSegregatedItems({});
		setSegregatedTotal(0);
	};
	const handleConfirmSave = () => {
		setConfirmModal(false);
		props.save({
			fromCenter: dc._id,
			fromKind: dc.kind,
			inventoryType,
			barcodes,
			items,
			segregatedItems,
			total: total + segregatedTotal,
		});
	};

	const handleFinishClick = () => {
		setConfirmModal(true);
	};

	const handleClose = () => {
		setConfirmModal(false);
	};

	const handleManualClick = () => {
		if (!manual) {
			setSinglePackageCountingAsBag(false);
		}
		setManual(!manual);
	};

	const renderDcs = () => (
		<Grid>
			{props.distributionCenters.loading ? (
				<LoadingDcs />
			) : (
				props.distributionCenters.data.map(dc => (
					<DcBox name={dc.name} onClick={handleClick} />
				))
			)}
		</Grid>
	);

	const renderInventoryType = () => (
		<Grid>
			{Object.values(inventoryTypes).map(type => (
				<DcBox name={type} onClick={handleSelectType} />
			))}
		</Grid>
	);
	const renderCounting = () => (
		<Grid>
			{step === 2 && !manual && (
				<SinglePackageAs20Radio
					onTypeChange={setSinglePackageCountingAsBag}
					type={singlePackageCountingAsBag}
				/>
			)}
			<ProductBox product={lastProduct} />
			<Totals total={total} segregated={segregatedTotal} />
			{(!inventoryType || inventoryType === Object.keys(inventoryTypes)[0]) && (
				<ManualButton onClick={handleManualClick} manual={manual} />
			)}
		</Grid>
	);

	const renderButtonsFooter = () => (
		<ButtonsFooter
			finishing={props.saving}
			onBack={handleBack}
			onFinish={handleFinishClick}
			disabled={!(step === 2 && total + segregatedTotal > 0)}
		/>
	);

	const renderConfirmModal = () => (
		<ConfirmModal open={confirmModal} onClick={handleConfirmSave} onClose={handleClose} />
	);

	const subTitleMessage = `${dc && dc.name} ${(inventoryType &&
		`| ${inventoryTypes[inventoryType]}`) ||
		''}`;
	return (
		<Grid container direction="column">
			<Title />
			<SubTitle>{(dc && subTitleMessage) || 'Escolha o local onde deseja começar'}</SubTitle>
			{step === 0 && renderDcs()}
			{step === 1 && renderInventoryType()}
			{step === 2 && renderCounting()}
			{step !== 0 && renderButtonsFooter()}
			{step === 2 && renderConfirmModal()}
		</Grid>
	);
};

const mapStateToProps = state => ({
	distributionCenters: state.distributionCenters,
	saving: state.counting.saving,
	saveError: state.counting.saveError,
	saveSuccess: state.counting.saveSuccess,
	products: state.productsLoss.products,
	rejectedBatches: state.productsLoss.rejectedBatches,
});

CountingContainer.propTypes = {
	loading: PropTypes.bool,
	error: PropTypes.bool,
	// `state.productsLoss.products` é um mapa indexado por sku (`products[sku]`, `Object.keys`),
	// não um array — mesma convenção do TransferCheckContainer.
	products: PropTypes.object,
	rejectedBatches: PropTypes.array,
	sendNotification: PropTypes.func.isRequired,
	errorNotification: PropTypes.func.isRequired,
	errorClear: PropTypes.func.isRequired,
	getProductsInfos: PropTypes.func.isRequired,
};

CountingContainer.defaultProps = {
	loading: false,
	error: false,
	products: {},
	rejectedBatches: [],
};

// Exportado sem conexão ao Redux para permitir testes de componente isolados.
export { CountingContainer };

export default connect(mapStateToProps, {
	selectDistributionCenter,
	save,
	fetchDistributionCenters,
	getProductsInfos,
	sendNotification,
	errorNotification,
	errorClear,
})(CountingContainer);
